import React from "react";
import { Box } from "@mui/material";

// Shared palette for diagrams (kept local so SVGs are self-contained).
const C = {
  ink: "#e2e8f0",
  soft: "#94a3b8",
  mute: "#64748b",
  indigo: "#818cf8",
  indigoFill: "rgba(99,102,241,0.14)",
  indigoStroke: "rgba(129,140,248,0.55)",
  cyan: "#22d3ee",
  cyanFill: "rgba(34,211,238,0.12)",
  cyanStroke: "rgba(34,211,238,0.5)",
  green: "#34d399",
  greenFill: "rgba(52,211,153,0.12)",
  greenStroke: "rgba(52,211,153,0.5)",
  amber: "#fbbf24",
  amberFill: "rgba(251,191,36,0.12)",
  amberStroke: "rgba(251,191,36,0.5)",
  rose: "#fb7185",
  roseFill: "rgba(251,113,133,0.12)",
  roseStroke: "rgba(251,113,133,0.5)",
  surface: "rgba(255,255,255,0.03)",
  border: "rgba(148,163,184,0.22)",
};

const mono = '"JetBrains Mono", ui-monospace, monospace';
const sans = '"Poppins", sans-serif';

// Responsive SVG wrapper — scales to container width, scrolls if too narrow.
const SvgFrame = ({ viewBox, children, minWidth = 640 }) => (
  <Box sx={{ width: "100%", overflowX: "auto", "&::-webkit-scrollbar": { height: 6 } }}>
    <svg
      viewBox={viewBox}
      style={{ width: "100%", minWidth, height: "auto", display: "block", fontFamily: sans }}
      role="img"
    >
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.soft} />
        </marker>
        <marker id="arrowCyan" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.cyan} />
        </marker>
        <marker id="arrowRose" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.rose} />
        </marker>
      </defs>
      {children}
    </svg>
  </Box>
);

// Rounded node helper
const Node = ({ x, y, w, h, fill, stroke, children, rx = 12 }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} stroke={stroke} strokeWidth="1.4" />
    {children}
  </g>
);

const T = ({ x, y, children, fill = C.ink, size = 14, weight = 600, family = sans, anchor = "middle" }) => (
  <text x={x} y={y} fill={fill} fontSize={size} fontWeight={weight} fontFamily={family} textAnchor={anchor} dominantBaseline="middle">
    {children}
  </text>
);

/* ============================================================
   1. Microservices Overview
   ============================================================ */
export const MicroservicesDiagram = () => {
  const services = [
    { n: "api-gateway", s: "FastAPI · 8000", d: "Auth, API keys, rate-limit, circuit breaker", c: "cyan" },
    { n: "user-service", s: "NestJS · 8001", d: "JWT + OAuth, API-key mgmt", c: "green" },
    { n: "mannequin-service", s: "NestJS · 8002", d: "Models, measurement profiles", c: "green" },
    { n: "garment-service", s: "NestJS · 8003", d: "Garment catalogue", c: "green" },
    { n: "vto-engine-service", s: "FastAPI · 8004", d: "Bulk sessions, Celery tasks", c: "indigo" },
    { n: "image-gen-service", s: "FastAPI · 8005", d: "7 providers, model selector", c: "indigo" },
    { n: "storage-service", s: "NestJS · 8006", d: "MinIO / S3, presigned URLs", c: "green" },
    { n: "vto-service", s: "FastAPI · 8007", d: "Try-on, CQRS, Observer", c: "indigo" },
  ];
  const fill = (c) => ({ indigo: C.indigoFill, cyan: C.cyanFill, green: C.greenFill }[c]);
  const stroke = (c) => ({ indigo: C.indigoStroke, cyan: C.cyanStroke, green: C.greenStroke }[c]);
  const label = (c) => ({ indigo: C.indigo, cyan: C.cyan, green: C.green }[c]);

  const cols = 4, cw = 232, ch = 96, gx = 20, gy = 24, x0 = 40, y0 = 150;

  return (
    <SvgFrame viewBox="0 0 1040 420" minWidth={860}>
      {/* Gateway bar */}
      <Node x={40} y={40} w={960} h={60} fill={C.cyanFill} stroke={C.cyanStroke} rx={14}>
        <T x={520} y={64} fill={C.cyan} size={16} weight={700}>API Gateway (FastAPI)</T>
        <T x={520} y={84} fill={C.soft} size={12} weight={500} family={mono}>JWT · B2B API keys · rate limiting · circuit breaker · routing</T>
      </Node>
      {/* connector from gateway down */}
      <line x1={520} y1={100} x2={520} y2={130} stroke={C.border} strokeWidth="1.4" markerEnd="url(#arrow)" />

      {services.slice(1).map((svc, i) => {
        const col = i % cols, row = Math.floor(i / cols);
        const x = x0 + col * (cw + gx);
        const y = y0 + row * (ch + gy);
        return (
          <Node key={svc.n} x={x} y={y} w={cw} h={ch} fill={fill(svc.c)} stroke={stroke(svc.c)}>
            <T x={x + cw / 2} y={y + 28} fill={label(svc.c)} size={14.5} weight={700} family={mono}>{svc.n}</T>
            <T x={x + cw / 2} y={y + 48} fill={C.soft} size={11.5} weight={500} family={mono}>{svc.s}</T>
            <T x={x + cw / 2} y={y + 72} fill={C.soft} size={11} weight={400}>{svc.d}</T>
          </Node>
        );
      })}

      {/* legend */}
      <g>
        <circle cx={44} cy={400} r={6} fill={C.indigoFill} stroke={C.indigoStroke} />
        <T x={132} y={400} fill={C.soft} size={12} anchor="middle">Python / FastAPI (AI)</T>
        <circle cx={252} cy={400} r={6} fill={C.greenFill} stroke={C.greenStroke} />
        <T x={330} y={400} fill={C.soft} size={12} anchor="middle">NestJS (business)</T>
        <T x={640} y={400} fill={C.mute} size={11.5} anchor="middle" family={mono}>database-per-service · no shared schema</T>
      </g>
    </SvgFrame>
  );
};

/* ============================================================
   2. Kafka + CQRS event flow
   ============================================================ */
export const KafkaCqrsDiagram = () => (
  <SvgFrame viewBox="0 0 1040 380" minWidth={820}>
    <T x={150} y={28} fill={C.green} size={13} weight={700} family={mono}>PRODUCERS · write side</T>
    <T x={520} y={28} fill={C.amber} size={13} weight={700} family={mono}>APACHE KAFKA</T>
    <T x={890} y={28} fill={C.indigo} size={13} weight={700} family={mono}>CONSUMER · read side (CQRS)</T>

    {/* producers */}
    <Node x={40} y={60} w={220} h={70} fill={C.greenFill} stroke={C.greenStroke}>
      <T x={150} y={88} fill={C.ink} size={14} family={mono}>mannequin-service</T>
      <T x={150} y={110} fill={C.soft} size={11.5}>creates / updates AI models</T>
    </Node>
    <Node x={40} y={160} w={220} h={70} fill={C.greenFill} stroke={C.greenStroke}>
      <T x={150} y={188} fill={C.ink} size={14} family={mono}>garment-service</T>
      <T x={150} y={210} fill={C.soft} size={11.5}>creates / updates garments</T>
    </Node>

    {/* kafka topics */}
    <Node x={400} y={55} w={240} h={80} fill={C.amberFill} stroke={C.amberStroke}>
      <T x={520} y={80} fill={C.amber} size={12.5} family={mono} weight={700}>vto.aimodel.*</T>
      <T x={520} y={102} fill={C.soft} size={11} family={mono}>created · updated · deleted</T>
    </Node>
    <Node x={400} y={155} w={240} h={80} fill={C.amberFill} stroke={C.amberStroke}>
      <T x={520} y={180} fill={C.amber} size={12.5} family={mono} weight={700}>vto.garment.*</T>
      <T x={520} y={202} fill={C.soft} size={11} family={mono}>created · updated · deleted</T>
    </Node>

    {/* consumer */}
    <Node x={780} y={90} w={230} h={90} fill={C.indigoFill} stroke={C.indigoStroke}>
      <T x={895} y={120} fill={C.indigo} size={13.5} family={mono} weight={700}>vto-service</T>
      <T x={895} y={142} fill={C.soft} size={11}>projects events into</T>
      <T x={895} y={158} fill={C.soft} size={11}>local read-models</T>
    </Node>

    {/* read models */}
    <Node x={780} y={215} w={105} h={56} fill={C.surface} stroke={C.border} rx={10}>
      <T x={832} y={243} fill={C.soft} size={11.5} family={mono}>AIModelRead</T>
    </Node>
    <Node x={905} y={215} w={105} h={56} fill={C.surface} stroke={C.border} rx={10}>
      <T x={957} y={243} fill={C.soft} size={11.5} family={mono}>GarmentRead</T>
    </Node>

    {/* note */}
    <Node x={740} y={295} w={295} h={60} fill="rgba(34,211,238,0.06)" stroke={C.cyanStroke} rx={10}>
      <T x={887} y={318} fill={C.cyan} size={11.5}>Reads served locally + Redis cache</T>
      <T x={887} y={336} fill={C.soft} size={10} family={mono}>HTTP fallback if event lags (eventual consistency)</T>
    </Node>

    {/* arrows */}
    <line x1={260} y1={95} x2={398} y2={95} stroke={C.green} strokeWidth="1.6" strokeDasharray="5 4" markerEnd="url(#arrow)" />
    <line x1={260} y1={195} x2={398} y2={195} stroke={C.green} strokeWidth="1.6" strokeDasharray="5 4" markerEnd="url(#arrow)" />
    <line x1={640} y1={95} x2={778} y2={125} stroke={C.amber} strokeWidth="1.6" strokeDasharray="5 4" markerEnd="url(#arrow)" />
    <line x1={640} y1={195} x2={778} y2={150} stroke={C.amber} strokeWidth="1.6" strokeDasharray="5 4" markerEnd="url(#arrow)" />
    <line x1={860} y1={180} x2={840} y2={213} stroke={C.indigo} strokeWidth="1.6" markerEnd="url(#arrow)" />
    <line x1={930} y1={180} x2={950} y2={213} stroke={C.indigo} strokeWidth="1.6" markerEnd="url(#arrow)" />
    <line x1={895} y1={271} x2={895} y2={293} stroke={C.cyan} strokeWidth="1.6" markerEnd="url(#arrowCyan)" />

    <T x={330} y={80} fill={C.green} size={11} family={mono}>publish</T>
    <T x={710} y={100} fill={C.amber} size={11} family={mono}>consume</T>
  </SvgFrame>
);

/* ============================================================
   3. Model Selector + Fallback (Strategy/Factory/Registry)
   ============================================================ */
export const ModelSelectorDiagram = () => (
  <SvgFrame viewBox="0 0 1040 360" minWidth={860}>
    <Node x={40} y={140} w={210} h={72} fill={C.cyanFill} stroke={C.cyanStroke}>
      <T x={145} y={168} fill={C.ink} size={13.5}>Request: generate image</T>
      <T x={145} y={190} fill={C.soft} size={11} family={mono}>(model_type, override?)</T>
    </Node>

    {/* decision 1 */}
    <g>
      <polygon points="360,176 430,140 500,176 430,212" fill={C.amberFill} stroke={C.amberStroke} strokeWidth="1.4" />
      <T x={430} y={170} fill={C.ink} size={12}>override</T>
      <T x={430} y={186} fill={C.ink} size={12}>provided?</T>
    </g>

    {/* decision 2 */}
    <g>
      <polygon points="360,300 430,266 500,300 430,334" fill={C.amberFill} stroke={C.amberStroke} strokeWidth="1.4" />
      <T x={430} y={294} fill={C.ink} size={12}>active model</T>
      <T x={430} y={310} fill={C.ink} size={12}>exists?</T>
    </g>

    <Node x={330} y={40} w={200} h={54} fill={C.surface} stroke={C.border} rx={10}>
      <T x={430} y={62} fill={C.soft} size={11.5}>Fallback: highest-</T>
      <T x={430} y={80} fill={C.soft} size={11.5}>priority model (DB)</T>
    </Node>

    {/* selected config */}
    <Node x={610} y={130} w={210} h={72} fill={C.indigoFill} stroke={C.indigoStroke}>
      <T x={715} y={158} fill={C.indigo} size={13.5} weight={700}>AIConfiguration</T>
      <T x={715} y={180} fill={C.soft} size={11}>provider + params</T>
    </Node>

    {/* factory */}
    <Node x={610} y={250} w={210} h={68} fill={C.surface} stroke={C.border} rx={10}>
      <T x={715} y={276} fill={C.ink} size={12.5} family={mono} weight={600}>ProviderFactory</T>
      <T x={715} y={298} fill={C.soft} size={10.5} family={mono}>create_provider()</T>
    </Node>

    {/* fallback manager */}
    <Node x={880} y={250} w={140} h={68} fill={C.roseFill} stroke={C.roseStroke} rx={10}>
      <T x={950} y={274} fill={C.rose} size={12} weight={700}>FallbackMgr</T>
      <T x={950} y={294} fill={C.soft} size={10}>try next on fail</T>
    </Node>

    {/* providers registry */}
    <Node x={610} y={40} w={410} h={56} fill="rgba(129,140,248,0.06)" stroke={C.indigoStroke} rx={10}>
      <T x={815} y={62} fill={C.soft} size={11} family={mono}>Registry: pollinations · openai · gemini</T>
      <T x={815} y={80} fill={C.soft} size={11} family={mono}>huggingface · replicate · imagen</T>
    </Node>

    {/* arrows */}
    <line x1={250} y1={176} x2={358} y2={176} stroke={C.soft} strokeWidth="1.5" markerEnd="url(#arrow)" />
    <line x1={500} y1={176} x2={608} y2={166} stroke={C.green} strokeWidth="1.5" markerEnd="url(#arrow)" />
    <T x={555} y={158} fill={C.green} size={11} family={mono}>yes</T>
    <line x1={430} y1={212} x2={430} y2={264} stroke={C.soft} strokeWidth="1.5" markerEnd="url(#arrow)" />
    <T x={444} y={240} fill={C.mute} size={11} family={mono} anchor="start">no</T>
    <line x1={500} y1={300} x2={715} y2={205} stroke={C.green} strokeWidth="1.5" markerEnd="url(#arrow)" />
    <T x={585} y={268} fill={C.green} size={11} family={mono}>yes</T>
    <line x1={430} y1={266} x2={430} y2={96} stroke={C.soft} strokeWidth="1.5" markerEnd="url(#arrow)" />
    <T x={360} y={120} fill={C.mute} size={11} family={mono}>no</T>
    <line x1={715} y1={202} x2={715} y2={248} stroke={C.indigo} strokeWidth="1.5" markerEnd="url(#arrow)" />
    <line x1={820} y1={284} x2={878} y2={284} stroke={C.rose} strokeWidth="1.5" strokeDasharray="5 4" markerEnd="url(#arrowRose)" />
  </SvgFrame>
);

/* ============================================================
   4. Circuit Breaker states
   ============================================================ */
export const CircuitBreakerDiagram = () => (
  <SvgFrame viewBox="0 0 900 240" minWidth={620}>
    <Node x={60} y={90} w={180} h={70} fill={C.greenFill} stroke={C.greenStroke} rx={35}>
      <T x={150} y={118} fill={C.green} size={16} weight={700}>CLOSED</T>
      <T x={150} y={140} fill={C.soft} size={11}>requests flow</T>
    </Node>
    <Node x={360} y={90} w={180} h={70} fill={C.roseFill} stroke={C.roseStroke} rx={35}>
      <T x={450} y={118} fill={C.rose} size={16} weight={700}>OPEN</T>
      <T x={450} y={140} fill={C.soft} size={11}>fail fast</T>
    </Node>
    <Node x={660} y={90} w={180} h={70} fill={C.amberFill} stroke={C.amberStroke} rx={35}>
      <T x={750} y={116} fill={C.amber} size={15} weight={700}>HALF-OPEN</T>
      <T x={750} y={138} fill={C.soft} size={11}>trial request</T>
    </Node>

    <line x1={240} y1={110} x2={358} y2={110} stroke={C.rose} strokeWidth="1.6" markerEnd="url(#arrowRose)" />
    <T x={300} y={96} fill={C.rose} size={11} family={mono}>failures ≥ N</T>

    <line x1={540} y1={130} x2={658} y2={130} stroke={C.amber} strokeWidth="1.6" markerEnd="url(#arrow)" />
    <T x={600} y={150} fill={C.amber} size={11} family={mono}>timeout</T>

    <path d="M 750 90 Q 470 20 160 88" fill="none" stroke={C.green} strokeWidth="1.6" markerEnd="url(#arrow)" />
    <T x={450} y={36} fill={C.green} size={11} family={mono}>success → reset</T>

    <path d="M 720 160 Q 585 210 480 162" fill="none" stroke={C.rose} strokeWidth="1.6" markerEnd="url(#arrowRose)" />
    <T x={600} y={205} fill={C.rose} size={11} family={mono}>fail → re-open</T>
  </SvgFrame>
);

/* ============================================================
   5. Result State Machine
   ============================================================ */
export const StateMachineDiagram = () => {
  const states = [
    { x: 40, label: "PENDING", c: C.soft, f: C.surface, s: C.border },
    { x: 230, label: "PROCESSING", c: C.amber, f: C.amberFill, s: C.amberStroke },
    { x: 440, label: "COMPLETED", c: C.cyan, f: C.cyanFill, s: C.cyanStroke },
    { x: 650, label: "SAVED", c: C.green, f: C.greenFill, s: C.greenStroke },
  ];
  return (
    <SvgFrame viewBox="0 0 860 200" minWidth={620}>
      {states.map((st) => (
        <Node key={st.label} x={st.x} y={70} w={160} h={56} fill={st.f} stroke={st.s} rx={28}>
          <T x={st.x + 80} y={98} fill={st.c} size={13.5} weight={700} family={mono}>{st.label}</T>
        </Node>
      ))}
      {[[200, 230], [390, 440], [600, 650]].map(([x1, x2], i) => (
        <line key={i} x1={x1} y1={98} x2={x2 - 2} y2={98} stroke={C.soft} strokeWidth="1.6" markerEnd="url(#arrow)" />
      ))}
      {/* failed */}
      <Node x={330} y={150} w={140} h={44} fill={C.roseFill} stroke={C.roseStroke} rx={22}>
        <T x={400} y={172} fill={C.rose} size={12.5} weight={700} family={mono}>FAILED</T>
      </Node>
      <line x1={310} y1={118} x2={380} y2={150} stroke={C.rose} strokeWidth="1.5" strokeDasharray="4 4" markerEnd="url(#arrowRose)" />
      <T x={430} y={40} fill={C.mute} size={11.5} anchor="middle" family={mono}>invalid transitions are rejected (e.g. FAILED → SAVED)</T>
    </SvgFrame>
  );
};

/* ============================================================
   6. LangGraph conversation state graph (Orchestrix)
   ============================================================ */
export const LangGraphDiagram = () => {
  // node: [x, y, label, colorKey]
  const nodes = [
    [40, 40, "Initialization", "gray"],
    [40, 130, "Greeting", "gray"],
    [40, 220, "Intent Routing", "indigo"],
    [340, 60, "New Order", "cyan"],
    [340, 150, "Customer Details", "cyan"],
    [340, 240, "Delivery Selection", "cyan"],
    [340, 330, "Order Review", "cyan"],
    [660, 90, "Manage Order", "green"],
    [660, 200, "Info Query", "green"],
    [660, 310, "Farewell", "amber"],
  ];
  const fill = { gray: C.surface, indigo: C.indigoFill, cyan: C.cyanFill, green: C.greenFill, amber: C.amberFill };
  const stroke = { gray: C.border, indigo: C.indigoStroke, cyan: C.cyanStroke, green: C.greenStroke, amber: C.amberStroke };
  const label = { gray: C.soft, indigo: C.indigo, cyan: C.cyan, green: C.green, amber: C.amber };
  const w = 200, h = 56;

  return (
    <SvgFrame viewBox="0 0 900 420" minWidth={760}>
      {/* backbone edges */}
      <line x1={140} y1={96} x2={140} y2={128} stroke={C.soft} strokeWidth="1.5" markerEnd="url(#arrow)" />
      <line x1={140} y1={186} x2={140} y2={218} stroke={C.soft} strokeWidth="1.5" markerEnd="url(#arrow)" />
      {/* intent routing branches out */}
      {[88, 178, 268, 358].map((y, i) => (
        <line key={i} x1={240} y1={248} x2={338} y2={y} stroke={C.indigo} strokeWidth="1.4" strokeDasharray="4 4" markerEnd="url(#arrow)" />
      ))}
      {/* routing to manage/info/farewell */}
      <line x1={240} y1={248} x2={658} y2={118} stroke={C.green} strokeWidth="1.4" strokeDasharray="4 4" markerEnd="url(#arrow)" />
      <line x1={240} y1={248} x2={658} y2={228} stroke={C.green} strokeWidth="1.4" strokeDasharray="4 4" markerEnd="url(#arrow)" />
      {/* order chain */}
      {[[88, 150], [178, 240], [268, 330]].map(([y1, y2], i) => (
        <line key={"c" + i} x1={440} y1={y1 + 56} x2={440} y2={y2} stroke={C.cyan} strokeWidth="1.4" markerEnd="url(#arrowCyan)" />
      ))}
      {/* review -> farewell */}
      <line x1={540} y1={358} x2={658} y2={338} stroke={C.amber} strokeWidth="1.4" markerEnd="url(#arrow)" />

      {nodes.map(([x, y, txt, c]) => (
        <Node key={txt} x={x} y={y} w={w} h={h} fill={fill[c]} stroke={stroke[c]} rx={12}>
          <T x={x + w / 2} y={y + h / 2 + 1} fill={label[c]} size={13.5} weight={600} family={mono}>{txt}</T>
        </Node>
      ))}

      {/* legend */}
      <T x={140} y={330} fill={C.mute} size={11} family={mono}>entry flow</T>
      <T x={440} y={410} fill={C.cyan} size={11} family={mono} anchor="middle">order-taking sub-flow (tools: search, cart, confirm)</T>
      <T x={760} y={370} fill={C.mute} size={11} family={mono} anchor="middle">stateful · cyclic · MemorySaver checkpoint</T>
    </SvgFrame>
  );
};

/* ============================================================
   7. WhatsApp message pipeline (Orchestrix)
   ============================================================ */
export const WhatsAppFlowDiagram = () => {
  const steps = [
    ["Customer", "WhatsApp message\n(text · image · voice)", "green"],
    ["Twilio", "webhook → NestJS\n/whatsapp/webhook", "rose"],
    ["Agent routing", "findByWhatsappNumber\n(multi-store)", "cyan"],
    ["LangGraph engine", "processMessage()\nvision · STT · LLM", "indigo"],
    ["Response", "text + product images\nor TTS voice reply", "amber"],
  ];
  const fill = { green: C.greenFill, rose: C.roseFill, cyan: C.cyanFill, indigo: C.indigoFill, amber: C.amberFill };
  const stroke = { green: C.greenStroke, rose: C.roseStroke, cyan: C.cyanStroke, indigo: C.indigoStroke, amber: C.amberStroke };
  const label = { green: C.green, rose: C.rose, cyan: C.cyan, indigo: C.indigo, amber: C.amber };
  const w = 176, h = 84, gap = 22, x0 = 20;

  return (
    <SvgFrame viewBox="0 0 1010 180" minWidth={880}>
      {steps.map(([title, sub, c], i) => {
        const x = x0 + i * (w + gap);
        const lines = sub.split("\n");
        return (
          <g key={title}>
            <Node x={x} y={45} w={w} h={h} fill={fill[c]} stroke={stroke[c]} rx={12}>
              <T x={x + w / 2} y={70} fill={label[c]} size={13.5} weight={700}>{title}</T>
              {lines.map((ln, j) => (
                <T key={j} x={x + w / 2} y={90 + j * 15} fill={C.soft} size={10.5} family={mono}>{ln}</T>
              ))}
            </Node>
            {i < steps.length - 1 && (
              <line x1={x + w} y1={87} x2={x + w + gap} y2={87} stroke={C.soft} strokeWidth="1.6" markerEnd="url(#arrow)" />
            )}
          </g>
        );
      })}
      <T x={505} y={150} fill={C.mute} size={11} anchor="middle" family={mono}>
        session keyed by phone number · IPv4-forced media fetch · daily rate-limit
      </T>
    </SvgFrame>
  );
};

/* ============================================================
   8. Shopify bi-directional sync (Orchestrix)
   ============================================================ */
export const ShopifySyncDiagram = () => (
  <SvgFrame viewBox="0 0 900 300" minWidth={680}>
    <Node x={60} y={110} w={240} h={90} fill={C.indigoFill} stroke={C.indigoStroke} rx={14}>
      <T x={180} y={142} fill={C.indigo} size={16} weight={700}>Orchestrix</T>
      <T x={180} y={165} fill={C.soft} size={11} family={mono}>products · customers</T>
      <T x={180} y={182} fill={C.soft} size={11} family={mono}>orders</T>
    </Node>
    <Node x={600} y={110} w={240} h={90} fill={C.greenFill} stroke={C.greenStroke} rx={14}>
      <T x={720} y={142} fill={C.green} size={16} weight={700}>Shopify</T>
      <T x={720} y={165} fill={C.soft} size={11} family={mono}>OAuth 2.0</T>
      <T x={720} y={182} fill={C.soft} size={11} family={mono}>webhooks</T>
    </Node>

    {/* PULL */}
    <path d="M 600 135 L 302 135" fill="none" stroke={C.green} strokeWidth="1.8" markerEnd="url(#arrow)" />
    <T x={450} y={122} fill={C.green} size={12} family={mono}>PULL · import existing</T>
    {/* PUSH */}
    <path d="M 300 175 L 598 175" fill="none" stroke={C.cyan} strokeWidth="1.8" markerEnd="url(#arrowCyan)" />
    <T x={450} y={195} fill={C.cyan} size={12} family={mono}>PUSH · export local (no shopifyId)</T>

    {/* webhook realtime */}
    <Node x={330} y={235} w={240} h={48} fill={C.surface} stroke={C.border} rx={10}>
      <T x={450} y={255} fill={C.soft} size={11} family={mono}>products/* · orders/* · customers/*</T>
      <T x={450} y={271} fill={C.mute} size={10} family={mono}>real-time via registered webhooks</T>
    </Node>
    <line x1={720} y1={200} x2={560} y2={250} stroke={C.amber} strokeWidth="1.5" strokeDasharray="4 4" markerEnd="url(#arrow)" />
    <line x1={330} y1={258} x2={302} y2={200} stroke={C.amber} strokeWidth="1.5" markerEnd="url(#arrow)" />

    <T x={450} y={40} fill={C.ink} size={14} weight={600} anchor="middle">Bi-directional synchronization</T>
  </SvgFrame>
);
